// İSG uzmanı portalı — ortak personel çekmecesi (GL5). Paylaşılan `PersonnelSheet` çerçevesinin EP
// içeriği; iki bağlamda AYNI belge listesini (`PersonnelDocuments`) gösterir:
//   - `registry`: taşeron Personel listesi satırı → durum / erişim etiketleri + "Düzenle" (#29),
//     tek bilgi ızgarası (`PersonnelFacts`: Telefon | E-posta / T.C. | tarih), belgeler
//     `GET .../personnel/{userUuid}/documents` ile (çekmece açıkken bağlanır, `enabled` bayrağı yok).
//   - `permit`: izin detayı Personel sekmesi → hazırlık / giriş etiketleri, eğitim-belge sayaçları,
//     eksik belge/eğitim rozetleri (eski S33 izin çekmecesi gövdesi), ardından iletişim ızgarası
//     (`PersonnelFacts`, T.C.'siz — izin DTO'sunda T.C. yok); belgeler izin satırından gelir
//     (`personnel.documents`; ayrı sorgu yok).
//
// **Düzenleme (#29).** `editor` çekmecenin SAHİBİNDE kurulur (`PersonnelPanel` / `PermitPersonnelPanel`:
// `usePersonnelEditor` + `usePersonnelClient`) ve `PersonnelSheet`'e geçer; düzenleme kipinde gövde
// yalnız formdur. Etiket satırındaki "Düzenle" (`PersonnelEditButton`) yalnız `toEditTarget` bir hedef
// verdiğinde (aktive personel, ham `isActive` boolean) görünür. İzin satırı `accessLevel`/`isActive`/
// T.C. taşımaz → izin bağlamında hedef taşeronun kayıt listesindeki satırdır (Personel sekmesiyle AYNI
// önbellek girdisi). Liste yüklenirken düğme kendi yükleniyor hâlindedir; yüklenemezse ya da kişi
// listede yoksa düğme yoktur ve etiketlerin altında tek soluk satır durumu söyler (Alert değil: ikincil
// bir özelliğin hatası çekmecenin asıl içeriğinin üstüne çıkmaz). 403'te (`contractors:get` yok)
// "Yeniden dene" yoktur — tekrar denemek sonucu değiştirmez.
//
// Çekmecede başka kişi düzeyi eylem YOK (Giriş İzni, Kapsamdan Çıkar, Sil listenin ⋮'sinde kalır):
// onlar `--z-dropdown`'da açılır, çekmecenin (`--z-drawer`) perdesinin altında kalırdı. Esc önce açık
// Reddet satırını kapatır (`escapeStaysInReviewRow`).

import {
  Alert,
  Button,
  type ContractorPersonnelDto,
  PersonnelEditButton,
  type PersonnelEditor,
  PersonnelFacts,
  PersonnelSheet,
  Spinner,
  Tag,
  type WorkPermitPersonnelDto,
  awaitsActivation,
  contractorsApi,
  currentCulture,
  isApiError,
  resolveApiErrorMessage,
  toEditTarget,
  useDocumentTypes,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { type ReactNode, type RefObject, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { missingTrainingChipLabels } from '../../lib/blockingReasons';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { formatDate, formatDateTime } from '../../lib/format';
import { type ReviewSource, escapeStaysInReviewRow } from '../documents/DocumentReviewActions';
import { PersonnelDocuments } from './PersonnelDocuments';
import { accessLevelTone, personnelStatusLabelKey, personnelStatusTone } from './tone';

export type PersonnelDrawerContext =
  | { kind: 'registry'; person: ContractorPersonnelDto }
  | { kind: 'permit'; person: WorkPermitPersonnelDto; source: ReviewSource };

export interface PersonnelDrawerProps {
  contractorUuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → kapalı ya da hedef artık yok. */
  context: PersonnelDrawerContext | null;
  /** Açan satırın "Ad" düğmesi — kapanışta odak buraya döner. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Düzenleme oturumu (#29) — sahip `usePersonnelEditor` ile kurar. Verilmezse "Düzenle" yoktur,
   * izin bağlamında kayıt listesi de çekilmez ve çekmece görünüm kipinde kalır.
   */
  editor?: PersonnelEditor;
}

export function PersonnelDrawer({
  contractorUuid,
  open,
  onOpenChange,
  context,
  restoreFocusRef,
  editor,
}: PersonnelDrawerProps) {
  const { t } = useTranslation();
  const permitPerson = context?.kind === 'permit' ? context.person : null;
  // İzin bağlamında Düzenle'nin hedefi: taşeronun kayıt listesi (Personel sekmesiyle aynı anahtar →
  // çoğunlukla önbellek isabeti). Kayıt bağlamında satır zaten elde; sorgu kapalı kalır.
  const registry = useQuery({
    queryKey: contractorsKeys.personnel(contractorUuid),
    queryFn: () => contractorsApi.listContractorPersonnel(contractorUuid),
    enabled: open && editor !== undefined && permitPerson !== null,
  });

  // Hedef her render'da GÜNCEL satırdan kurulur; Düzenle `begin`'e onu tıklama anında verir (F2).
  let actions: ReactNode = null;
  let hydrateNotice: ReactNode = null;
  if (editor && context?.kind === 'registry') {
    const target = toEditTarget(context.person);
    actions = target ? <PersonnelEditButton editor={editor} target={target} /> : null;
  } else if (editor && permitPerson !== null) {
    const uuid = permitPerson.uuid.toLowerCase();
    // Başarısız arka plan tazelemesi eldeki listeyi silmez → önce veri.
    const row = registry.data?.find((p) => p.kind === 'Personnel' && p.uuid.toLowerCase() === uuid);
    const target = row ? toEditTarget(row) : null;
    if (target) {
      actions = <PersonnelEditButton editor={editor} target={target} />;
    } else if (registry.data !== undefined) {
      // Veri varken eşleşme yoksa satır arka plan tazelemesinde de durur (yükleme düğmesiyle
      // titreşmez); tazeleme eşleşme getirirse üstteki dal Düzenle'yi kendiliğinden gösterir.
      hydrateNotice = <HydrateLine>{t('personnelForm.hydrate.notInRegistry')}</HydrateLine>;
    } else if (registry.data === undefined && registry.isError) {
      hydrateNotice =
        isApiError(registry.error) && registry.error.statusCode === 403 ? (
          // `permits:get` var, `contractors:get` yok: yeniden denemek sonucu değiştirmez.
          <HydrateLine>{resolveApiErrorMessage(registry.error, t)}</HydrateLine>
        ) : (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <HydrateLine>{t('personnelForm.hydrate.failed')}</HydrateLine>
            <Button variant="ghost" size="sm" onClick={() => void registry.refetch()}>
              {t('personnelForm.hydrate.retry')}
            </Button>
          </div>
        );
    } else {
      actions = <PersonnelEditButton editor={editor} loading />;
    }
  }

  return (
    <PersonnelSheet
      open={open}
      onOpenChange={onOpenChange}
      person={context?.person ?? null}
      restoreFocusRef={restoreFocusRef}
      onEscapeKeyDown={escapeStaysInReviewRow}
      editor={editor}
      personUuid={context?.person.uuid}
      actions={actions}
      notice={(context ? editor?.notice(context.person.uuid) : null) ?? hydrateNotice}
      tags={
        context === null ? undefined : context.kind === 'registry' ? (
          <>
            <Tag color={personnelStatusTone(context.person.status)}>
              {t(personnelStatusLabelKey(context.person))}
            </Tag>
            <Tag color={accessLevelTone(context.person.accessLevel)}>
              {t(`contractors.personnel.access.${context.person.accessLevel.toLowerCase()}`)}
            </Tag>
          </>
        ) : (
          <>
            <Tag color={context.person.ready ? 'green' : 'red'}>
              {context.person.ready
                ? t('permits.personnel.ready')
                : t('permits.personnel.notReady')}
            </Tag>
            <Tag color={context.person.entryPermittedAt !== null ? 'green' : 'gray'}>
              {context.person.entryPermittedAt !== null
                ? t('permits.personnel.siteAccess.granted')
                : t('permits.personnel.siteAccess.notGranted')}
            </Tag>
          </>
        )
      }
      summary={
        context === null ? undefined : context.kind === 'registry' ? (
          <RegistrySummary person={context.person} />
        ) : (
          <PermitSummary person={context.person} />
        )
      }
      documents={
        context === null ? null : context.kind === 'registry' ? (
          <RegistryDocuments contractorUuid={contractorUuid} userUuid={context.person.uuid} />
        ) : (
          <PersonnelDocuments
            contractorUuid={contractorUuid}
            userUuid={context.person.uuid}
            documents={Array.isArray(context.person.documents) ? context.person.documents : null}
            source={context.source}
          />
        )
      }
    />
  );
}

/**
 * İzin bağlamında Düzenle'nin yerini alan tek soluk satır (yüklenemedi / 403 / kayıtta yok). Durumu
 * renk değil metin taşır; `<output>` (örtük `role="status"`, `Spinner`/`HeaderSearch` deseni): çekmece
 * açıldıktan sonra gezinmesiz belirebilir.
 */
function HydrateLine({ children }: { children: ReactNode }) {
  return (
    <output className="flex items-start gap-1.5 text-xs text-muted">
      <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span>{children}</span>
    </output>
  );
}

/**
 * Tek ızgara (`PersonnelFacts`): Telefon | E-posta / T.C. | tarih. Maskeli değerler düz metindir ve
 * maske bildirimi ızgaranın hemen altındadır; tarih hücresi `extra` olarak AYNI ızgaraya girer.
 */
function RegistrySummary({ person }: { person: ContractorPersonnelDto }) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const tcLabelId = useId();
  return (
    <PersonnelFacts
      email={person.email}
      phone={person.phone}
      personalDataMasked={person.personalDataMasked}
      tc={{ value: person.tcKimlikNo, labelId: tcLabelId }}
      extra={
        <div className="min-w-0">
          <dt className="text-xs font-medium text-muted">
            {person.status === 'NoLogin'
              ? t('contractors.personnel.drawer.createdAt')
              : t('contractors.personnel.drawer.activatedAt')}
          </dt>
          <dd className="tnum mt-0.5 text-sm text-fg">{formatDate(person.activatedAt, culture)}</dd>
        </div>
      }
    />
  );
}

function RegistryDocuments({
  contractorUuid,
  userUuid,
}: {
  contractorUuid: string;
  userUuid: string;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: contractorsKeys.personnelDocuments(contractorUuid, userUuid),
    queryFn: () => contractorsApi.listPersonnelDocuments(contractorUuid, userUuid),
  });
  if (query.isPending) {
    return <Spinner label={t('contractors.personnelDocs.loading')} />;
  }
  // Başarısız arka plan tazelemesi eldeki listeyi silmez; Alert yalnız veri hiç yokken.
  if (query.isError && query.data === undefined) {
    return <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>;
  }
  return (
    <PersonnelDocuments
      contractorUuid={contractorUuid}
      userUuid={userUuid}
      documents={query.data ?? null}
      source={query}
    />
  );
}

/**
 * S33 izin hazırlık gövdesi — satırdakiyle AYNI türetme (`PermitPersonnelPanel`). Ardından, Belgeler'in
 * hemen üstünde, dört bağlamda aynı yerde duran iletişim ızgarası (T.C.'siz; maske bildirimi dahil).
 */
function PermitSummary({ person }: { person: WorkPermitPersonnelDto }) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const culture = currentCulture();
  // Eksik eğitimler belgeler gibi tek tek rozetlenir (§35.3); dashboard "Aksiyon gerekli" ile aynı kaynak.
  const trainingLabels = awaitsActivation(person)
    ? [t('permits.personnel.notActivated')]
    : missingTrainingChipLabels(
        person.missingTrainings.map((training) => training.title),
        person.completedTrainingCount < person.requiredTrainingCount,
        t,
      );
  return (
    <>
      <dl className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted">{t('permits.personnel.readiness.training')}</dt>
          <dd className="tnum text-sm text-fg">
            {person.completedTrainingCount} / {person.requiredTrainingCount}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted">{t('permits.personnel.readiness.documents')}</dt>
          <dd className="tnum text-sm text-fg">
            {person.satisfiedDocumentCount} / {person.requiredDocumentCount}
          </dd>
        </div>
      </dl>

      {person.missingDocumentTypes.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-muted">{t('permits.personnel.missingDocuments')}</p>
          <ul className="flex flex-wrap gap-1.5">
            {person.missingDocumentTypes.map((type) => (
              <li key={type}>
                <Tag color="red" wrap>
                  {label('personnel', type)}
                </Tag>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {trainingLabels.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-muted">{t('permits.personnel.missingTrainings')}</p>
          <ul className="flex flex-wrap gap-1.5">
            {trainingLabels.map((label) => (
              <li key={label}>
                <Tag color="red">{label}</Tag>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {person.entryPermittedAt !== null ? (
        <p className="text-xs text-muted">
          {t('permits.entry.grantedMeta', {
            date: formatDateTime(person.entryPermittedAt, culture),
            by: person.entryPermittedByName ?? '—',
          })}
        </p>
      ) : null}

      <PersonnelFacts
        email={person.email}
        phone={person.phone}
        personalDataMasked={person.personalDataMasked}
      />
    </>
  );
}
